// God-mode API client — Bearer auth, relative /api (internal nginx proxies to
// the backend over the SSM tunnel). The token lives in sessionStorage: it
// survives a page refresh but dies with the tab, and is dropped once its JWT
// `exp` passes or the API answers 401.
const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const SS_KEY = "sc_god_token";

function tokenExpired(t) {
  try {
    const { exp } = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return !exp || exp * 1000 <= Date.now() + 30_000;
  } catch {
    return true;
  }
}

function loadToken() {
  try {
    const t = sessionStorage.getItem(SS_KEY);
    if (t && !tokenExpired(t)) return t;
    sessionStorage.removeItem(SS_KEY);
  } catch { /* storage blocked — in-memory only */ }
  return null;
}

let _token = loadToken();
let _onAuthError = null;

export function getToken() {
  if (_token && tokenExpired(_token)) setToken(null);
  return _token;
}
export function setToken(t) {
  _token = t || null;
  try {
    if (_token) sessionStorage.setItem(SS_KEY, _token);
    else sessionStorage.removeItem(SS_KEY);
  } catch { /* ignore */ }
}
/** GodApp registers this to drop back to the login screen on any 401. */
export function onAuthError(cb) {
  _onAuthError = cb;
}

async function req(path, init = {}) {
  const headers = { "Content-Type": "application/json", ...(init.headers || {}) };
  const tok = getToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  const txt = await res.text();
  let data = null;
  if (txt) { try { data = JSON.parse(txt); } catch { data = txt; } }
  if (!res.ok) {
    if (res.status === 401) { setToken(null); if (_onAuthError) _onAuthError(); }
    const detail = (data && data.detail) || (typeof data === "string" ? data : "Request failed");
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = res.status;
    throw err;
  }
  return data;
}

function qs(params) {
  const p = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.set(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const god = {
  async login(email, password) {
    const r = await req("/auth/token", { method: "POST", body: JSON.stringify({ email, password }) });
    if (r.user?.role !== "superadmin") {
      throw new Error("Not authorized for god mode");
    }
    setToken(r.access_token);
    return r.user;
  },
  logout() { setToken(null); },
  overview: () => req("/admin/overview"),
  status: () => req("/admin/status"),
  versions: () => req("/admin/versions"),
  users: (params) => req(`/admin/users${qs(params)}`), // {page,page_size,q} → paged envelope
  createUser: (body) => req("/admin/users", { method: "POST", body: JSON.stringify(body) }),
  merchant: (id) => req(`/admin/merchants/${id}`),
  merchantTransactions: (id, params) => req(`/admin/merchants/${id}/transactions${qs(params)}`),
  updateMerchant: (id, body) => req(`/admin/merchants/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  suspend: (id) => req(`/admin/merchants/${id}/suspend`, { method: "POST" }),
  reactivate: (id) => req(`/admin/merchants/${id}/reactivate`, { method: "POST" }),
  addUpi: (id, body) => req(`/admin/merchants/${id}/upi-ids`, { method: "POST", body: JSON.stringify(body) }),
  updateUpi: (id, upiId, body) => req(`/admin/merchants/${id}/upi-ids/${upiId}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteUpi: (id, upiId) => req(`/admin/merchants/${id}/upi-ids/${upiId}`, { method: "DELETE" }),
  devices: (params) => req(`/admin/devices${qs(params)}`), // {page,page_size} → paged envelope
  revokeDevice: (id) => req(`/admin/devices/${id}/revoke`, { method: "POST" }),
  // Notification browser / analyzer.
  notifications: (params) => req(`/admin/notifications${qs(params)}`),
  notificationStats: (params) => req(`/admin/notifications/stats${qs(params)}`),
  notificationFacets: () => req("/admin/notifications/facets"),
  // Per-app capture coverage (which layer — text / a11y / ocr — carried each credit)
  // and the gap-detection sweep (registered but silent devices).
  notificationCoverage: (params) => req(`/admin/notifications/coverage${qs(params)}`),
  deviceGaps: (hours) => req(`/admin/device-gaps${qs({ hours })}`),
  // Live parser rules (zero-downtime edit).
  parserConfig: () => req("/admin/parser-config"),
  saveParserConfig: (config) => req("/admin/parser-config", { method: "PUT", body: JSON.stringify({ config }) }),
  testParserConfig: (body) => req("/admin/parser-config/test", { method: "POST", body: JSON.stringify(body) }),
  resetParserConfig: () => req("/admin/parser-config/reset", { method: "POST" }),
  // SuperCash Gateway (shared ProtonPay account).
  gwOverview: () => req("/admin/gateway/overview"),
  gwSettings: () => req("/admin/gateway/settings"),
  gwSaveSettings: (body) => req("/admin/gateway/settings", { method: "PUT", body: JSON.stringify(body) }),
  gwTestSettings: () => req("/admin/gateway/settings/test", { method: "POST" }),
  gwMerchants: (params) => req(`/admin/gateway/merchants${qs(params)}`),
  gwSaveMerchant: (id, body) => req(`/admin/gateway/merchants/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  gwAdjust: (id, body) => req(`/admin/gateway/merchants/${id}/adjust`, { method: "POST", body: JSON.stringify(body) }),
  // kind: payins | payouts | callbacks | deliveries | ledger
  gwList: (kind, params) => req(`/admin/gateway/${kind}${qs(params)}`),
  gwDetail: (kind, id) => req(`/admin/gateway/${kind}/${id}`),
  gwRefresh: (kind, id) => req(`/admin/gateway/${kind}/${id}/refresh`, { method: "POST" }),
  gwRecheckPending: () => req("/admin/gateway/payins/recheck", { method: "POST" }),
  gwRecheckProgress: () => req("/admin/gateway/payins/recheck"),
  gwResolve: (id, body) => req(`/admin/gateway/payouts/${id}/resolve`, { method: "POST", body: JSON.stringify(body) }),
};
